import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ApiDfsContest } from '@/api/dfs';
import { leagueTextClasses, leagueCardRingClasses, leagueCtaClasses } from '@/utils/leagueColors';
import { buildDfsContestSectionHref } from '@/utils/dfsContestNavigation';

type ContestCardMode = 'active' | 'upcoming' | 'entered';

interface ContestCardProps {
  contest: ApiDfsContest;
  mode: ContestCardMode;
  /** The combined flagship wears the house-gold featured treatment. */
  featured?: boolean;
}

/* ── Helpers ─────────────────────────────────────────────── */

function formatContestDate(contest: ApiDfsContest): string {
  const iso = contest.start_time || contest.contest_date;
  if (!iso) return 'Time TBA';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'Time TBA';
    const day = d.toLocaleDateString(undefined, { weekday: 'short' });
    const month = d.toLocaleDateString(undefined, { month: 'short' });
    const date = d.getDate();
    if (contest.start_time) {
      const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      return `${day}, ${month} ${date} · ${time}`;
    }
    return `${day}, ${month} ${date}`;
  } catch {
    return 'Time TBA';
  }
}


/** Time-to-kickoff label. Under 1h: "Starts in 38m"; under 24h: "Starts in
 *  5h 12m"; 24h+ collapses to DAYS ONLY ("Starts in 3 days") — never
 *  "72h 11m". Returns null once started (the LIVE/ACTIVE badges take over).
 *  Exported for LeagueSummaryCard (dashboard) — same voice for league drafts. */
export function formatCountdown(startIso: string | null | undefined, nowMs: number): string | null {
  if (!startIso) return null;
  const start = new Date(startIso).getTime();
  if (isNaN(start)) return null;
  const diffMs = start - nowMs;
  if (diffMs <= 0) return null;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Starts in <1m';
  if (mins < 60) return `Starts in ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Starts in ${hours}h ${mins % 60}m`;
  const days = Math.floor(hours / 24);
  return `Starts in ${days} day${days === 1 ? '' : 's'}`;
}

// Exported for LeagueSummaryCard — same ordinal voice on both surfaces.
export function formatRank(rank: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = rank % 100;
  return rank + (s[(v - 20) % 10] || s[v] || s[0]);
}

function getCta(
  mode: ContestCardMode,
  hasLineup: boolean,
  canStillBuild: boolean,
  isLive: boolean,
  isCompleted: boolean,
) {
  if (mode === 'entered') {
    if (isCompleted) {
      return { label: 'View Results', path: (id: number) => buildDfsContestSectionHref(id, 'leaderboard'), show: true };
    }
    if (isLive) {
      return { label: 'View Live', path: (id: number) => buildDfsContestSectionHref(id, 'leaderboard'), show: true };
    }
    if (canStillBuild) {
      return { label: 'Edit Lineup', path: (id: number) => `/dfs/build/${id}`, show: true };
    }
    return { label: 'View Lineup', path: (id: number) => buildDfsContestSectionHref(id, 'lineup'), show: true };
  }
  if (mode === 'upcoming') {
    return {
      label: hasLineup ? 'Edit Lineup' : 'Build Lineup',
      path: (id: number) => `/dfs/build/${id}`,
      show: true,
    };
  }
  // active mode — only show CTA if there are still unstarted matches
  if (canStillBuild) {
    return { label: 'Build Lineup', path: (id: number) => `/dfs/build/${id}`, show: true };
  }
  // All locked: card navigates to contest detail but no button shown
  return { label: '', path: (id: number) => buildDfsContestSectionHref(id, 'leaderboard'), show: false };
}

/** For 'entered' mode, determine if the contest is live right now */
function isContestLive(contest: ApiDfsContest, nowMs: number): boolean {
  if (contest.is_finalized) return false;
  if (!contest.start_time) return false;
  return new Date(contest.start_time).getTime() <= nowMs;
}

/* ── Component ───────────────────────────────────────────── */

const ContestCard: React.FC<ContestCardProps> = ({ contest, mode }) => {
  const navigate = useNavigate();
  const hasLineup = !!contest.user_lineup_id;
  const isWeekend = !!contest.is_weekend_contest;

  // Tick once a minute so countdowns and the pregame → live CTA transition
  // stay honest if the lobby remains open through kickoff.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const lastMatchMs = contest.last_match_start_time
    ? new Date(contest.last_match_start_time).getTime()
    : null;
  const canStillBuild = lastMatchMs == null || !Number.isFinite(lastMatchMs) || lastMatchMs > nowMs;
  const isLive = mode === 'entered' ? isContestLive(contest, nowMs) : mode === 'active';
  const isCompleted = mode === 'entered' && !!contest.is_finalized;
  const cta = getCta(mode, hasLineup, canStillBuild, isLive, isCompleted);
  const ctaLabel = cta.label;
  const ctaPath = cta.path(contest.id);
  const leaderboardPath = buildDfsContestSectionHref(contest.id, 'leaderboard');
  // Once a lineup exists, the card itself opens the social payoff. Building
  // and editing remain explicit CTA actions, so tapping a submitted contest
  // never drops the user back into work they just completed.
  const cardPath = hasLineup || mode === 'entered' || !canStillBuild
    ? leaderboardPath
    : ctaPath;

  // League — shown exactly once. Combined slates carry EVERY member
  // competition ("EPL + LA LIGA Weekend Slate") - taking [0] alone mislabeled
  // the combined contest as a plain La Liga slate (caught 2026-07-21, first
  // combined weekend in the lobby). Sorted by id so EPL (1) leads.
  const league = (contest.competitions ?? [])
    .slice()
    .sort((a, b) => (a.id ?? 99) - (b.id ?? 99))
    .map(c => c.short_name || c.name)
    .filter(Boolean)
    .join(' + ');

  // Contest type label — constructed, never from raw name
  const typeLabel = isWeekend ? 'Weekend Slate' : 'Daily';

  // Date — shown exactly once
  const dateDisplay = formatContestDate(contest);

  // Meta chips
  const matchCount = contest.relevant_match_ids?.length || 0;
  const metaParts: string[] = [];
  if (matchCount > 0) metaParts.push(`${matchCount} Match${matchCount !== 1 ? 'es' : ''}`);
  if (mode !== 'entered' && contest.total_entries != null) {
    metaParts.push(`${contest.total_entries.toLocaleString()} Entr${contest.total_entries !== 1 ? 'ies' : 'y'}`);
  }

  // Countdown is shown only before kickoff; live state owns that space after.
  const showCountdown = mode === 'upcoming' || (mode === 'entered' && !isLive && !isCompleted);
  const countdown = showCountdown ? formatCountdown(contest.start_time, nowMs) : null;

  // League identity is stable across every contest state. Semantic badges and
  // CTA copy communicate lineup/live/final status without repainting the card.
  const borderStyle = leagueCardRingClasses(contest.competitions);

  const cardBg = 'bg-gradient-to-r from-surface-card to-surface-raised';

  // Title color: weekend titles ("Weekend Slate") wear the league color;
  // dailies keep light blue (superuser-only surface).
  const titleColor = isWeekend
    ? (leagueTextClasses(contest.competitions) || 'text-accent-yellow')
    : 'text-accent-light-blue';
  const scoreColor = leagueTextClasses(contest.competitions) || 'text-accent-yellow';

  // Muted opacity for completed contests in entered mode
  const cardOpacity = isCompleted ? 'opacity-70' : '';

  return (
    <li className="list-none">
      <div
        className={`
          flex items-center justify-between
          rounded-xl border border-gray-700/60
          ${borderStyle} ${cardBg} ${cardOpacity}
          hover:brightness-110 active:brightness-90 transition-all duration-150
          shadow-sm hover:shadow-md
          min-h-[112px] px-3 py-3 sm:px-4 sm:py-4
          cursor-pointer
        `}
        onClick={() => navigate(cardPath)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') navigate(cardPath); }}
      >
        {/* Left: title + date + meta */}
        <div className="flex-1 min-w-0 pr-3">
          {/* Row 1: one restrained league accent plus semantic status. */}
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {league && (
              <span className={`font-heading font-bold text-sm sm:text-base uppercase flex-shrink-0 ${leagueTextClasses(contest.competitions) || titleColor}`}>
                {league}
              </span>
            )}
            <h3 className={`font-heading font-bold text-sm sm:text-base truncate ${titleColor}`}>
              {typeLabel}
            </h3>
            {isLive && (
              <span className="px-1.5 py-0.5 text-micro font-bold bg-green-600/20 text-green-400 ring-1 ring-green-500/30 rounded-full flex-shrink-0">
                LIVE
              </span>
            )}
            {mode === 'entered' && !isLive && !isCompleted && (
              <span className="flex-shrink-0 rounded-full bg-green-600/20 px-1.5 py-0.5 text-micro font-bold text-green-400 ring-1 ring-green-500/30">
                LINEUP SET
              </span>
            )}
            {mode === 'upcoming' && (
              <span className="flex-shrink-0 rounded-full bg-gray-500/15 px-1.5 py-0.5 text-micro font-bold text-gray-300 ring-1 ring-gray-500/30">
                UPCOMING
              </span>
            )}
            {isCompleted && (
              <span className="px-1.5 py-0.5 text-micro font-bold bg-red-600/20 text-red-400 ring-1 ring-red-500/30 rounded-full flex-shrink-0">
                FINAL
              </span>
            )}
          </div>

          {/* Row 2: Date/time + countdown */}
          <p className="mt-1 text-[12px] text-gray-300 sm:text-sm">
            {dateDisplay}
            {countdown && (
              <span className="font-semibold text-white"> · {countdown}</span>
            )}
          </p>

          {/* Row 3: Meta (match count, salary cap, entries) */}
          {metaParts.length > 0 && (
            <p className="text-caption sm:text-[12px] text-white mt-1">
              {metaParts.join(' · ')}
            </p>
          )}

          {/* Row 4: User score/rank (when available) */}
          {contest.user_score != null && (
            <p className={`mt-0.5 text-caption font-semibold ${scoreColor}`}>
              {contest.user_score.toFixed(1)} PTs
              {contest.user_rank != null && contest.total_entries != null && (
                <span className="text-gray-400 font-normal">
                  {' '}· {formatRank(contest.user_rank)} / {contest.total_entries.toLocaleString()}
                </span>
              )}
            </p>
          )}
        </div>

        {/* Right: CTA — only rendered when applicable */}
        {cta.show && (
          <div className="flex-shrink-0">
            <Link
              to={ctaPath}
              onClick={(e) => e.stopPropagation()}
              className={`
                inline-block
                font-heading font-bold uppercase
                text-xs tracking-wide
                py-2.5 px-5 sm:px-6
                rounded-lg
                shadow-sm
                transition-colors duration-150
                active:scale-95
                text-center
                whitespace-nowrap
                ${leagueCtaClasses(contest.competitions)}
              `}
              aria-label={`${ctaLabel} contest`}
            >
              {ctaLabel}
            </Link>
          </div>
        )}
      </div>
    </li>
  );
};

export default ContestCard;
